import {memo} from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import {cn} from '@features/ui/utils';
import {useActiveStructure} from '@features/frame/frameHook';
import {useLikeFeedback} from '@features/frame/hooks/useLikeFeedback';
import {useFeedback} from '@features/frame/hooks/useFeedback';
import {useAiThinkingForChat} from '@features/ai/useAiThinking';
import {BananaSpinner} from '@features/ui/components/BananaSpinner';
import {FeedbackModal} from './FeedbackModal';
import ThumbsUpEmpty from '@/icons/thumbs-up-empty.svg';
import ThumbsUpFilled from '@/icons/thumbs-up-filled.svg';
import ThumbsDownEmpty from '@/icons/thumbs-down-empty.svg';
import ThumbsDownFilled from '@/icons/thumbs-down-filled.svg';

type CanvasFeedbackButtonsProps = {
  screenId: string;
  flowId: string;
  variant?: 'toolbar' | 'standalone';
};

export const useHasActiveStructure = (screenId: string) => {
  const {activeStructure} = useActiveStructure(screenId);
  return activeStructure?.id !== undefined;
};

export const CanvasFeedbackButtons = memo(
  ({screenId, flowId, variant = 'toolbar'}: CanvasFeedbackButtonsProps) => {
    const {activeStructure} = useActiveStructure(screenId);
    const structureId = activeStructure?.id;
    const screenshotUrl = activeStructure?.screenshotUrl;
    const isAiThinking = useAiThinkingForChat(screenId);

    const {isLoading, isLiked, isDisliked, submitFeedback} =
      useLikeFeedback(structureId);

    const {
      isModalOpen,
      activeAction,
      handleLike,
      handleDislike,
      closeModal,
      handleSubmit,
    } = useFeedback({
      flowId,
      screenId,
      structureId,
      analyticsSource: 'Canvas',
      screenshotUrl,
    });

    const isProcessing = activeAction !== null;

    const handleLikeClick = () => {
      if (!structureId || isLoading || isAiThinking || isProcessing) return;
      submitFeedback(true);
      handleLike();
    };

    const handleDislikeClick = () => {
      if (!structureId || isLoading || isAiThinking || isProcessing) return;
      submitFeedback(false);
      handleDislike();
    };

    const isDisabled =
      isLoading || !structureId || isAiThinking || isProcessing;

    // Don't render for empty screens (no active structure)
    if (!structureId) return null;

    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                'flex items-center justify-center rounded-md transition-colors',
                variant === 'standalone'
                  ? 'h-5 w-5 text-gray-400'
                  : 'h-7 w-7 text-white',
                isDisabled
                  ? 'cursor-default opacity-50'
                  : variant === 'standalone'
                    ? 'hover:text-gray-500'
                    : 'hover:bg-white/20',
              )}
              onClick={handleLikeClick}
              aria-label='Like this version'
              aria-pressed={isLiked}
              aria-disabled={isDisabled}
            >
              {activeAction === 'like' ? (
                <BananaSpinner width={16} height={16} />
              ) : isLiked && !isAiThinking ? (
                <ThumbsUpFilled />
              ) : (
                <ThumbsUpEmpty />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side='bottom' sideOffset={8}>
            Good AI output
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={cn(
                'flex items-center justify-center rounded-md transition-colors',
                variant === 'standalone'
                  ? 'h-5 w-5 text-gray-400'
                  : 'h-7 w-7 text-white',
                isDisabled
                  ? 'cursor-default opacity-50'
                  : variant === 'standalone'
                    ? 'hover:text-gray-500'
                    : 'hover:bg-white/20',
              )}
              onClick={handleDislikeClick}
              aria-label='Dislike this version'
              aria-pressed={isDisliked}
              aria-disabled={isDisabled}
            >
              {activeAction === 'dislike' ? (
                <BananaSpinner width={16} height={16} />
              ) : isDisliked && !isAiThinking ? (
                <ThumbsDownFilled />
              ) : (
                <ThumbsDownEmpty />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side='bottom' sideOffset={8}>
            Bad AI output
          </TooltipContent>
        </Tooltip>

        <FeedbackModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmit}
        />
      </>
    );
  },
);
CanvasFeedbackButtons.displayName = 'CanvasFeedbackButtons';
